import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { TAppThunk, TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import {
  HUB_FEATURE_KEYS,
  type HubConfiguration,
  type HubFeature,
  type HubFeatureId,
} from 'types/hubFeatures.type';
import type { IDefaultReducerState } from 'types/state.interface';
import { DrawerPayloadAction } from 'types/utils.type';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import api from '../services/api.service';

export const hubFeatureName = (id: HubFeatureId | undefined) =>
  HUB_FEATURE_KEYS.find((item) => item.id === id)?.defaultName || '-';

const hubConfigurationEndpoint = '/hub/configurations';

export type THubFeaturesState = IDefaultReducerState & {
  data: HubFeature[];
  selected: HubFeature | null;
};

export const initialState: THubFeaturesState = {
  loading: false,
  hasErrors: false,
  data: [],
  selected: null,
};

const slice = createSlice({
  name: 'hubFeatures',
  initialState,
  reducers: {
    fetchHubFeaturesRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchHubFeaturesSuccess: (
      state,
      { payload }: PayloadAction<HubConfiguration['features']>
    ) => {
      state.data = HUB_FEATURE_KEYS.map((item) => ({
        id: item.id,
        enabled: payload[item.id].enabled,
      }));
      finishedLoadingSuccess(state);
    },
    fetchHubFeaturesFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateHubFeatureRequest: (state) => {
      isLoadingRequest(state);
    },
    updateHubFeatureSuccess: (
      state,
      { payload }: PayloadAction<HubFeature>
    ) => {
      state.data = state.data.map((item) =>
        item.id === payload.id ? { ...item, ...payload } : item
      );
      state.selected = { ...state.selected, ...payload };
      finishedLoadingSuccess(state);
    },
    updateHubFeatureFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateHubFeatureValue: <Key extends keyof HubFeature>(
      state: THubFeaturesState,
      { payload }: DrawerPayloadAction<HubFeature, Key>
    ) => {
      const { key, value } = payload;
      state.selected = {
        ...state.selected,
        [key]: value,
      };
    },
    hubFeatureDiscardChanges: (
      state,
      { payload }: PayloadAction<{ id: HubFeatureId }>
    ) => {
      state.selected = state.data.find((item) => item.id === payload.id);
    },
    setSelectedFeature: (
      state,
      { payload }: PayloadAction<HubFeatureId | undefined>
    ) => {
      state.selected = state.data.find((dashboard) => dashboard.id === payload);
    },
  },
});

export const {
  fetchHubFeaturesRequest,
  fetchHubFeaturesSuccess,
  fetchHubFeaturesFailure,
  updateHubFeatureRequest,
  updateHubFeatureSuccess,
  updateHubFeatureFailure,
  updateHubFeatureValue,
  hubFeatureDiscardChanges,
  setSelectedFeature,
} = slice.actions;

export const hubFeaturesSelector = (state: TRootState) =>
  state.hubFeatures.data;

export const hubFeatureLoadingSelector = (state: TRootState) =>
  state.hubFeatures.loading;

export const selectedHubFeaturesSelector = (state: TRootState) =>
  state.hubFeatures.selected;

export const fetchHubFeatures = createAppAsyncThunk(
  'hubFeatures/fetchHubFeatures',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchHubFeaturesRequest());
      const result = await api.get<undefined, HubConfiguration>({
        endpoint: hubConfigurationEndpoint,
      });
      dispatch(fetchHubFeaturesSuccess(result.features));
    } catch (e) {
      dispatch(fetchHubFeaturesFailure());
      console.error(e);
    }
  }
);

export const saveHubFeature = createAppAsyncThunk(
  'hubFeatures/saveHubFeature',
  async (feature: HubFeature, { dispatch }) => {
    try {
      dispatch(updateHubFeatureRequest());
      const result = await api.put<
        Pick<HubFeature, 'enabled'>,
        HubConfiguration
      >({
        endpoint: `${hubConfigurationEndpoint}/features/${feature.id}`,
        data: { enabled: feature.enabled },
      });
      dispatch(
        updateHubFeatureSuccess({
          id: feature.id,
          enabled: result.features[feature.id].enabled,
        })
      );
    } catch (e) {
      dispatch(updateHubFeatureFailure());
      console.error(e);
    }
  }
);

export const checkDisabled = (): TAppThunk<boolean> => (dispatch, getState) => {
  try {
    const { hubFeatures } = getState();
    return checkSavedDisabled(
      hubFeatures.selected,
      hubFeatures.data.find((f) => f.id === hubFeatures.selected?.id)
    );
  } catch (e) {
    console.error(e);
    return false;
  }
};

export default slice.reducer;
